import { createOperationId } from '../../domain/operationId';
import { DBSchema, deleteDB, IDBPDatabase, openDB } from 'idb';
import {
  RapidCaptureAuthority,
  RapidCaptureDraft,
  RapidCaptureRecord,
  RapidCaptureStats,
  sanitizeRapidCaptureTerminalRecord,
  CaptureDraftFields,
  hasCaptureEdits,
  hasPendingCaptureAI,
  ItemEditOptions,
} from './rapidCaptureTypes';
import { Item } from '../../domain/item';
import { validateKeywords } from '../../domain/keywords';
import { recordRapidCaptureMetric } from './rapidCaptureMetrics';
import { processImage } from '../../services/imageProcessing';

export const RAPID_CAPTURE_DB_NAME = 'qkindex-rapid-capture';
export const RAPID_CAPTURE_DB_VERSION = 6;
export const RAPID_CAPTURE_STORE = 'captures';
export const RAPID_CAPTURE_CHANGED_EVENT = 'qkindex:rapid-capture-changed';
export const MAX_PENDING_CAPTURES = 100;
export const MAX_PENDING_BYTES = 250 * 1024 * 1024;
const AUTHORITY_PURGE_PREFIX = 'qkindex.pending-authority-purge.';

const preparePhoto = (image: Blob) =>
  processImage(
    image instanceof File
      ? image
      : new File([image], 'item.jpg', { type: image.type }),
  );

const draftEdits = (
  current: RapidCaptureRecord,
  fields: Partial<CaptureDraftFields>,
  image: Blob | null | undefined,
  options: ItemEditOptions = {},
): RapidCaptureRecord => {
  const localRevision = (current.localRevision ?? 0) + 1;
  return {
    ...current,
    edits: { ...current.edits, ...fields },
    localRevision,
    editFieldRevisions: {
      ...current.editFieldRevisions,
      ...Object.fromEntries(
        Object.keys(fields).map((key) => [key, localRevision]),
      ),
    },
    ...(image === undefined
      ? {}
      : { pendingImage: image, pendingImageRevision: localRevision }),
    ...(options.reviewedRevision === undefined
      ? {}
      : {
          itemRevision: options.reviewedRevision,
          editSnapshot: undefined,
        }),
    mergeKeywords: options.mergeKeywords ?? current.mergeKeywords ?? true,
    ...(image === undefined
      ? {}
      : {
          analyzeImage: false,
          aiDismissed: true,
          controlAction:
            current.itemCreated && current.analyzeImage
              ? ('save' as const)
              : current.controlAction,
        }),
    lastError:
      current.lastError?.phase === 'ai' ? current.lastError : undefined,
    editAttempts: 0,
    state:
      current.state === 'failed' && current.lastError?.phase !== 'ai'
        ? current.itemCreated
          ? current.completion
            ? 'confirming'
            : 'analysis-submitting'
          : 'pending'
        : current.state,
    nextAttemptAt: undefined,
    updatedAt: new Date().toISOString(),
  };
};

interface AuthorityPurgeTombstone {
  userId: string;
  collectionId: string;
  observedMembershipVersion: number;
  reason: 'revoked' | 'membership-changed' | 'collection-deleting';
}

interface RapidCaptureSchema extends DBSchema {
  captures: {
    key: string;
    value: RapidCaptureRecord;
    indexes: {
      'by-user': string;
      'by-user-state': [string, string];
      'by-authority': [string, string];
      'by-authority-state': [string, string, string];
    };
  };
}

let databasePromise: Promise<IDBPDatabase<RapidCaptureSchema>> | null = null;

const database = () => {
  if (typeof indexedDB === 'undefined')
    throw new Error('Durable browser storage is unavailable.');
  if (!databasePromise) {
    databasePromise = openDB<RapidCaptureSchema>(
      RAPID_CAPTURE_DB_NAME,
      RAPID_CAPTURE_DB_VERSION,
      {
        upgrade(db, oldVersion, _newVersion, transaction) {
          if (!db.objectStoreNames.contains(RAPID_CAPTURE_STORE)) {
            const store = db.createObjectStore(RAPID_CAPTURE_STORE, {
              keyPath: 'operationId',
            });
            store.createIndex('by-user', 'userId');
            store.createIndex('by-user-state', ['userId', 'state']);
            store.createIndex('by-authority', ['userId', 'collectionId']);
            store.createIndex('by-authority-state', [
              'userId',
              'collectionId',
              'state',
            ]);
          } else if (oldVersion < 2) {
            const store = transaction.objectStore(RAPID_CAPTURE_STORE);
            if (!store.indexNames.contains('by-authority')) {
              store.createIndex('by-authority', ['userId', 'collectionId']);
            }
            if (!store.indexNames.contains('by-authority-state')) {
              store.createIndex('by-authority-state', [
                'userId',
                'collectionId',
                'state',
              ]);
            }
            // Keep pending photos from older clients for explicit recovery.
          }
          if (db.objectStoreNames.contains('metadata' as never))
            db.deleteObjectStore('metadata' as never);
        },
      },
    );
  }
  return databasePromise;
};

const changed = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(RAPID_CAPTURE_CHANGED_EVENT));
    // Notify sibling tabs without publishing photo, field, or authority data.
    try {
      localStorage.setItem(
        RAPID_CAPTURE_CHANGED_EVENT,
        `${Date.now()}:${Math.random()}`,
      );
    } catch {
      /* Optional wakeup. */
    }
  }
};

export const recordBytes = (record: RapidCaptureRecord) =>
  [
    ...new Set([
      record.image,
      record.pendingImage,
      record.editSnapshot?.image,
      record.publishedImage,
    ]),
  ].reduce((bytes, image) => bytes + (image?.size ?? 0), 0) +
  ((record as unknown as { labelScanImage?: Blob }).labelScanImage?.size || 0);

const authorityPurgeKey = (userId: string, collectionId: string) =>
  `${AUTHORITY_PURGE_PREFIX}${encodeURIComponent(userId)}.${encodeURIComponent(collectionId)}`;

export const rapidCaptureDb = {
  async list(userId: string): Promise<RapidCaptureRecord[]> {
    const records = await (
      await database()
    ).getAllFromIndex(RAPID_CAPTURE_STORE, 'by-user', userId);
    return records.sort((left, right) =>
      left.createdAt.localeCompare(right.createdAt),
    );
  },

  async get(operationId: string): Promise<RapidCaptureRecord | undefined> {
    return (await database()).get(RAPID_CAPTURE_STORE, operationId);
  },

  async stats(
    userId: string,
    collectionId?: string,
  ): Promise<RapidCaptureStats> {
    const records = (await this.list(userId)).filter(
      (record) => !collectionId || record.collectionId === collectionId,
    );
    return {
      pending: records.filter(({ state }) => state === 'pending').length,
      active: records.filter(({ state }) => state === 'submitting').length,
      analysisProcessing: records.filter(
        ({ state }) => state === 'analysis-submitting',
      ).length,
      confirming: records.filter(({ state }) => state === 'confirming').length,
      failed: records.filter(
        ({ state }) => state === 'failed' || state === 'terminal',
      ).length,
      total: records.length,
      bytes: records.reduce((total, record) => total + recordBytes(record), 0),
    };
  },

  async enqueue(
    authority: RapidCaptureAuthority,
    draft: RapidCaptureDraft,
    acceptedAiCostVersion?: string,
    aiSkipReason?: RapidCaptureRecord['aiSkipReason'],
    retainedItemId?: string,
  ): Promise<RapidCaptureRecord> {
    if (
      !authority.userId ||
      !authority.collectionId ||
      authority.compatibilityMode !== 'collection-only' ||
      !Number.isSafeInteger(authority.membershipVersion) ||
      authority.membershipVersion < 0
    ) {
      throw new Error('Rapid Capture authority is unavailable.');
    }
    const startedAt = performance.now();
    const location = draft.location.trim();
    if (!location)
      throw new Error('Choose a location before accepting a capture.');
    if (!Number.isFinite(draft.qty) || draft.qty < 1) {
      throw new Error('Quantity must be at least 1.');
    }
    const image = await processImage(
      draft.image instanceof File
        ? draft.image
        : new File([draft.image], 'capture.jpg', { type: draft.image.type }),
    );
    const incomingBytes = image.size;
    const operationId = createOperationId();
    const analysisOperationId = draft.analyzeImage
      ? createOperationId()
      : undefined;
    const now = new Date().toISOString();
    const record: RapidCaptureRecord = {
      schemaVersion: 6,
      localRevision: 0,
      syncedRevision: 0,
      mergeKeywords: true,
      operationId,
      userId: authority.userId,
      collectionId: authority.collectionId,
      membershipVersion: authority.membershipVersion,
      ...(authority.lifecycleVersion === undefined
        ? {}
        : { lifecycleVersion: authority.lifecycleVersion }),
      compatibilityMode: authority.compatibilityMode,
      itemId: retainedItemId || createOperationId(),
      image,
      analyzeImage: draft.analyzeImage,
      keywords: validateKeywords(draft.keywords),
      location,
      locationId: draft.locationId,
      qty: draft.qty,
      ...(draft.tagIds === undefined ? {} : { tagIds: [...draft.tagIds] }),
      state: 'pending',
      itemCreated: false,
      itemCreateAttempted: false,
      attempts: 0,
      createdAt: now,
      updatedAt: now,
      analysisOperationId,
      ...(analysisOperationId && acceptedAiCostVersion
        ? { acceptedAiCostVersion }
        : {}),
      ...(aiSkipReason ? { aiSkipReason } : {}),
    };
    const db = await database();
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    const currentRecords = await transaction.store
      .index('by-user')
      .getAll(authority.userId);
    if (
      currentRecords.length >= MAX_PENDING_CAPTURES ||
      currentRecords.reduce(
        (bytes, current) => bytes + recordBytes(current),
        0,
      ) +
        incomingBytes >
        MAX_PENDING_BYTES
    ) {
      await transaction.done;
      throw new Error(
        'The Rapid Capture queue is full. Let queued items upload before capturing more.',
      );
    }
    await transaction.store.put(record);
    await transaction.done;
    recordRapidCaptureMetric(
      'indexeddb-enqueue',
      performance.now() - startedAt,
      {
        bytes: incomingBytes,
        analysis: Boolean(draft.analyzeImage),
      },
    );
    changed();
    return record;
  },

  async enqueueEdit(
    authority: RapidCaptureAuthority,
    item: Item,
    input: Partial<CaptureDraftFields>,
    photo?: Blob | null,
    options: ItemEditOptions = {},
  ): Promise<RapidCaptureRecord> {
    if (
      !authority.userId ||
      !authority.collectionId ||
      authority.compatibilityMode !== 'collection-only' ||
      !Number.isSafeInteger(authority.membershipVersion) ||
      authority.membershipVersion < 0 ||
      item.collectionId !== authority.collectionId ||
      item.deleted
    )
      throw new Error('Item editing access is unavailable.');
    const fields = { ...input };
    if (fields.keywords !== undefined)
      fields.keywords = validateKeywords(fields.keywords);
    if (
      fields.qty !== undefined &&
      (!Number.isFinite(fields.qty) || fields.qty < 0)
    )
      throw new Error('Quantity must be zero or more.');
    if (fields.location !== undefined && !fields.location.trim())
      throw new Error('Choose an available location.');
    const image = photo instanceof Blob ? await preparePhoto(photo) : photo;
    const transaction = (await database()).transaction(
      RAPID_CAPTURE_STORE,
      'readwrite',
    );
    const records = await transaction.store
      .index('by-user')
      .getAll(authority.userId);
    const previous = records.find(
      (record) =>
        record.schemaVersion === 6 &&
        record.itemId === item.id &&
        record.collectionId === authority.collectionId &&
        record.membershipVersion === authority.membershipVersion &&
        record.lifecycleVersion === authority.lifecycleVersion &&
        record.state !== 'terminal',
    );
    if (previous?.itemUnavailable)
      throw new Error('The saved item is no longer available.');
    if (
      previous?.leaseOwner?.startsWith('action:') &&
      (previous.leaseExpiresAt ?? 0) > Date.now()
    )
      throw new Error(
        'Pending changes are being discarded. Try saving again shortly.',
      );
    if (
      previous?.lastError?.category === 'conflict' &&
      previous.lastError.phase !== 'ai' &&
      options.reviewedRevision === undefined
    )
      throw new Error(
        'Review the saved values before resolving this conflict.',
      );
    const revision = options.reviewedRevision ?? item.revision;
    if (!previous && (!Number.isSafeInteger(revision) || revision! < 0))
      throw new Error(
        'The saved item revision is unavailable. Reopen this item.',
      );
    const now = new Date().toISOString();
    const rebasing =
      previous?.mutation &&
      !previous.itemCreated &&
      options.reviewedRevision !== undefined;
    const queuedImage =
      image !== undefined
        ? image
        : rebasing
          ? previous.pendingImage !== undefined
            ? previous.pendingImage
            : previous.mutation!.imageAction === 'remove'
              ? null
              : previous.mutation!.imageAction === 'replace'
                ? previous.image
                : undefined
          : undefined;
    const record: RapidCaptureRecord =
      previous && !rebasing
        ? draftEdits(previous, fields, image, options)
        : {
            schemaVersion: 6,
            operationId: createOperationId(),
            itemId: item.id,
            userId: authority.userId,
            collectionId: authority.collectionId,
            membershipVersion: authority.membershipVersion,
            lifecycleVersion: authority.lifecycleVersion,
            compatibilityMode: authority.compatibilityMode,
            keywords: item.keywords,
            location: item.location,
            locationId: item.locationId,
            qty: item.qty,
            tagIds: item.tagIds,
            mutation: {
              expectedRevision: revision!,
              imageAction:
                queuedImage === null
                  ? 'remove'
                  : queuedImage
                    ? 'replace'
                    : 'keep',
              mergeKeywords: options.mergeKeywords ?? false,
            },
            mutationFields: {
              ...(rebasing
                ? { ...previous.mutationFields, ...previous.edits }
                : {}),
              ...fields,
            },
            image: queuedImage ?? new Blob([]),
            localRevision: 0,
            syncedRevision: 0,
            mergeKeywords: options.mergeKeywords ?? false,
            state: 'pending',
            itemCreated: false,
            attempts: 0,
            createdAt: now,
            updatedAt: now,
          };
    const totalBytes = records
      .filter((entry) => entry !== previous)
      .reduce(
        (bytes, entry) => bytes + recordBytes(entry),
        recordBytes(record),
      );
    if (
      (!previous && records.length >= MAX_PENDING_CAPTURES) ||
      totalBytes > MAX_PENDING_BYTES
    )
      throw new Error(
        'The item activity queue is full. Let pending changes sync before saving more.',
      );
    if (rebasing) await transaction.store.delete(previous.operationId);
    await transaction.store.put(record);
    await transaction.done;
    changed();
    return record;
  },

  async put(record: RapidCaptureRecord): Promise<void> {
    const db = await database();
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    const existing = await transaction.store.get(record.operationId);
    if (
      existing &&
      (existing.userId !== record.userId ||
        existing.collectionId !== record.collectionId ||
        existing.membershipVersion !== record.membershipVersion ||
        existing.compatibilityMode !== record.compatibilityMode)
    ) {
      throw new Error('Rapid Capture queue authority is immutable.');
    }
    if (existing?.state === 'terminal' && record.state !== 'terminal') {
      await transaction.done;
      throw new Error('Rapid Capture terminal state is immutable.');
    }
    await transaction.store.put({
      ...record,
      updatedAt: new Date().toISOString(),
    });
    await transaction.done;
    changed();
  },

  /** Merge a receipt into the current entry atomically; never revive removed or revoked work. */
  async update(
    identity: RapidCaptureRecord,
    reduce: (current: RapidCaptureRecord) => RapidCaptureRecord | undefined,
  ): Promise<RapidCaptureRecord | undefined> {
    const transaction = (await database()).transaction(
      RAPID_CAPTURE_STORE,
      'readwrite',
    );
    const current = await transaction.store.get(identity.operationId);
    if (
      !current ||
      current.state === 'terminal' ||
      current.userId !== identity.userId ||
      current.collectionId !== identity.collectionId ||
      current.itemId !== identity.itemId ||
      current.membershipVersion !== identity.membershipVersion ||
      current.lifecycleVersion !== identity.lifecycleVersion ||
      current.compatibilityMode !== identity.compatibilityMode
    ) {
      await transaction.done;
      return undefined;
    }
    const next = reduce(current);
    if (next === current) {
      await transaction.done;
      return current;
    }
    if (
      next &&
      (next.userId !== current.userId ||
        next.collectionId !== current.collectionId ||
        next.itemId !== current.itemId ||
        next.operationId !== current.operationId ||
        next.membershipVersion !== current.membershipVersion ||
        next.lifecycleVersion !== current.lifecycleVersion)
    ) {
      await transaction.done;
      throw new Error('Rapid Capture queue authority is immutable.');
    }
    if (next)
      await transaction.store.put({
        ...next,
        updatedAt: new Date().toISOString(),
      });
    else await transaction.store.delete(identity.operationId);
    await transaction.done;
    changed();
    return next;
  },

  async remove(operationId: string): Promise<void> {
    await (await database()).delete(RAPID_CAPTURE_STORE, operationId);
    changed();
  },

  /** The IndexedDB write transaction is the cross-tab upload/edit claim. */
  async claim(
    identity: RapidCaptureRecord,
    owner: string,
  ): Promise<RapidCaptureRecord | undefined> {
    let claimed = false;
    const value = await this.update(identity, (current) => {
      if (
        current.leaseExpiresAt &&
        current.leaseExpiresAt > Date.now() &&
        current.leaseOwner !== owner
      )
        return current;
      claimed = true;
      return {
        ...current,
        leaseOwner: owner,
        leaseExpiresAt: Date.now() + 60_000,
      };
    });
    return claimed ? value : undefined;
  },

  async release(identity: RapidCaptureRecord, owner: string): Promise<void> {
    await this.update(identity, (current) =>
      current.leaseOwner !== owner
        ? current
        : { ...current, leaseOwner: undefined, leaseExpiresAt: undefined },
    );
  },

  async holdDraft(
    identity: RapidCaptureRecord,
    owner: string,
    hold: boolean,
  ): Promise<void> {
    await this.update(identity, (current) => {
      const editorLeases = Object.fromEntries(
        Object.entries(current.editorLeases ?? {}).filter(
          ([, expiry]) => expiry > Date.now(),
        ),
      );
      if (hold) editorLeases[owner] = Date.now() + 90_000;
      else delete editorLeases[owner];
      return {
        ...current,
        editorLeases,
      };
    });
  },

  async removeCompleted(identity: RapidCaptureRecord): Promise<boolean> {
    let removed = false;
    await this.update(identity, (current) => {
      if (
        current.state !== 'confirming' ||
        !current.itemCreated ||
        hasCaptureEdits(current) ||
        hasPendingCaptureAI(current) ||
        Object.values(current.editorLeases ?? {}).some(
          (expiry) => expiry > Date.now(),
        )
      )
        return current;
      removed = true;
      return undefined;
    });
    return removed;
  },

  async resetForSession(userId: string, collectionId?: string): Promise<void> {
    const records = await this.list(userId);
    await Promise.all(
      records
        .filter(
          (record) => !collectionId || record.collectionId === collectionId,
        )
        .filter(
          ({ state }) =>
            state !== 'pending' &&
            state !== 'failed' &&
            state !== 'confirming' &&
            state !== 'terminal',
        )
        .filter(
          (record) =>
            !record.leaseExpiresAt || record.leaseExpiresAt <= Date.now(),
        )
        .map((record) =>
          this.put({
            ...record,
            state: record.itemCreated
              ? hasPendingCaptureAI(record)
                ? 'analysis-submitting'
                : 'confirming'
              : 'pending',
            leaseOwner: undefined,
            leaseExpiresAt: undefined,
          }),
        ),
    );
  },

  async terminalizeCollectionAuthority(
    userId: string,
    collectionId: string,
    observedMembershipVersion: number,
    reason: 'revoked' | 'membership-changed' | 'collection-deleting',
  ): Promise<number> {
    const db = await database();
    const records = await db.getAllFromIndex(
      RAPID_CAPTURE_STORE,
      'by-authority',
      [userId, collectionId],
    );
    const affected = records.filter(
      (record) =>
        (reason === 'collection-deleting' ||
          record.membershipVersion !== observedMembershipVersion) &&
        record.state !== 'terminal',
    );
    if (!affected.length) return 0;
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    await Promise.all(
      affected.map((record) =>
        transaction.store.put(
          sanitizeRapidCaptureTerminalRecord(record, reason, {
            category: 'permission',
            message:
              reason === 'revoked'
                ? 'Collection membership was revoked.'
                : reason === 'collection-deleting'
                  ? 'Collection deletion has started.'
                  : 'Collection membership changed.',
          }),
        ),
      ),
    );
    await transaction.done;
    changed();
    return affected.length;
  },

  async terminalizeCollectionLifecycle(
    userId: string,
    collectionId: string,
    lifecycleVersion: number,
  ): Promise<number> {
    const db = await database();
    const records = await db.getAllFromIndex(
      RAPID_CAPTURE_STORE,
      'by-authority',
      [userId, collectionId],
    );
    const affected = records.filter(
      (record) =>
        record.lifecycleVersion !== lifecycleVersion &&
        record.state !== 'terminal',
    );
    if (!affected.length) return 0;
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    await Promise.all(
      affected.map((record) =>
        transaction.store.put(
          sanitizeRapidCaptureTerminalRecord(record, 'collection-deleting', {
            category: 'permission',
            message: 'Collection lifecycle changed.',
          }),
        ),
      ),
    );
    await transaction.done;
    changed();
    return affected.length;
  },

  async deleteCollectionAuthority(
    userId: string,
    collectionId: string,
    observedMembershipVersion: number,
    includeCurrentVersion = false,
  ): Promise<number> {
    const db = await database();
    const records = await db.getAllFromIndex(
      RAPID_CAPTURE_STORE,
      'by-authority',
      [userId, collectionId],
    );
    const affected = records.filter(
      (record) =>
        includeCurrentVersion ||
        record.membershipVersion !== observedMembershipVersion,
    );
    if (!affected.length) return 0;
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    await Promise.all(
      affected.map(({ operationId }) => transaction.store.delete(operationId)),
    );
    await transaction.done;
    changed();
    return affected.length;
  },

  async purgeCollectionAuthority(
    userId: string,
    collectionId: string,
    observedMembershipVersion: number,
    reason: 'revoked' | 'membership-changed' | 'collection-deleting',
  ): Promise<number> {
    if (typeof localStorage === 'undefined') {
      throw new Error('Durable authority cleanup state is unavailable.');
    }
    const tombstone: AuthorityPurgeTombstone = {
      userId,
      collectionId,
      observedMembershipVersion,
      reason,
    };
    const key = authorityPurgeKey(userId, collectionId);
    let tombstonePersisted = false;
    try {
      localStorage.setItem(key, JSON.stringify(tombstone));
      tombstonePersisted = true;
    } catch {
      // Continue with exact sanitization even when marker persistence is unavailable.
    }
    let affected: number;
    try {
      affected = await this.terminalizeCollectionAuthority(
        userId,
        collectionId,
        observedMembershipVersion,
        reason,
      );
    } catch {
      affected = await this.deleteCollectionAuthority(
        userId,
        collectionId,
        observedMembershipVersion,
        reason === 'collection-deleting',
      );
    }
    if (tombstonePersisted) localStorage.removeItem(key);
    return affected;
  },

  async retryPendingAuthorityPurges(userId: string): Promise<number> {
    if (typeof localStorage === 'undefined') return 0;
    const prefix = `${AUTHORITY_PURGE_PREFIX}${encodeURIComponent(userId)}.`;
    const keys = Array.from({ length: localStorage.length }, (_, index) =>
      localStorage.key(index),
    )
      .filter((key): key is string => Boolean(key?.startsWith(prefix)))
      .slice(0, 100);
    let affected = 0;
    for (const key of keys) {
      let tombstone: AuthorityPurgeTombstone;
      try {
        tombstone = JSON.parse(
          localStorage.getItem(key) || '',
        ) as AuthorityPurgeTombstone;
      } catch {
        localStorage.removeItem(key);
        continue;
      }
      if (
        tombstone.userId !== userId ||
        !tombstone.collectionId ||
        !Number.isSafeInteger(tombstone.observedMembershipVersion) ||
        tombstone.observedMembershipVersion < 0 ||
        !['revoked', 'membership-changed', 'collection-deleting'].includes(
          tombstone.reason,
        )
      ) {
        localStorage.removeItem(key);
        continue;
      }
      affected += await this.purgeCollectionAuthority(
        tombstone.userId,
        tombstone.collectionId,
        tombstone.observedMembershipVersion,
        tombstone.reason,
      );
    }
    return affected;
  },

  async reconcileCollectionAuthorities(
    userId: string,
    authorities: readonly {
      collectionId: string;
      membershipVersion?: number;
      deleting?: boolean;
      lifecycleVersion?: number;
    }[],
  ): Promise<number> {
    let affected = 0;
    for (const authority of authorities.slice(0, MAX_PENDING_CAPTURES)) {
      if (authority.lifecycleVersion !== undefined) {
        affected += await this.terminalizeCollectionLifecycle(
          userId,
          authority.collectionId,
          authority.lifecycleVersion,
        );
      }
      if (authority.deleting && authority.membershipVersion !== undefined) {
        affected += await this.purgeCollectionAuthority(
          userId,
          authority.collectionId,
          authority.membershipVersion,
          'collection-deleting',
        );
      } else if (authority.membershipVersion === undefined) {
        affected += await this.terminalizeCollectionAuthority(
          userId,
          authority.collectionId,
          -1,
          'revoked',
        );
      } else {
        affected += await this.terminalizeCollectionAuthority(
          userId,
          authority.collectionId,
          authority.membershipVersion,
          'membership-changed',
        );
      }
    }
    return affected;
  },

  subscribe(listener: () => void): () => void {
    window.addEventListener(RAPID_CAPTURE_CHANGED_EVENT, listener);
    const crossTab = (event: StorageEvent) => {
      if (event.key === RAPID_CAPTURE_CHANGED_EVENT) listener();
    };
    window.addEventListener('storage', crossTab);
    return () => {
      window.removeEventListener(RAPID_CAPTURE_CHANGED_EVENT, listener);
      window.removeEventListener('storage', crossTab);
    };
  },
};

/** The move controller consumes the same durable save receipt; no alternate writer. */
export const waitForCaptureSync = (
  accepted: RapidCaptureRecord,
): Promise<void> =>
  new Promise((resolve, reject) => {
    let settled = false;
    const finish = (error?: unknown) => {
      if (settled) return;
      settled = true;
      unsubscribe();
      window.removeEventListener('offline', offline);
      if (error) reject(error);
      else resolve();
    };
    const offline = () =>
      finish(
        new Error(
          'The changes are saved on this device. Reconnect before moving the item.',
        ),
      );
    const check = () => {
      void rapidCaptureDb.get(accepted.operationId).then((current) => {
        if (!current || current.state === 'terminal' || current.itemUnavailable)
          return finish(
            new Error(
              'The pending save is no longer available. Reopen the item before moving it.',
            ),
          );
        if (
          current.itemCreated &&
          (current.syncedRevision ?? 0) >= (accepted.localRevision ?? 0)
        )
          return finish();
        if (current.state === 'failed' && current.lastError?.phase !== 'ai')
          finish(
            new Error(
              current.lastError?.message ||
                'Resolve item activity before moving this item.',
            ),
          );
      }, finish);
    };
    const unsubscribe = rapidCaptureDb.subscribe(check);
    window.addEventListener('offline', offline);
    if (navigator.onLine) check();
    else offline();
  });

export const resetRapidCaptureDatabaseForTests = async () => {
  if (databasePromise) (await databasePromise).close();
  databasePromise = null;
  await deleteDB(RAPID_CAPTURE_DB_NAME);
};
